import React, { useEffect, useRef, useState } from "react";
import {
  Text,
  Modal,
  createStyles,
  Group,
  Button,
  Box,
  Loader,
} from "@mantine/core";
import { MonthPickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import moment from "moment";
import { useSelector } from "react-redux";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { getMar } from "../../../services";
import { decrypt, getActiveSlotForDate, getActiveSlotsForDate } from "../../../utils";

const SLOT_ORDER = [
  "6AM - 10AM",
  "10AM - 2PM",
  "2PM - 6PM",
  "6PM - 10PM",
  "10PM - 2AM",
  "2AM - 6AM",
];

const STATUS_COLORS = {
  taken: "#4CAF50",
  missed: "#EF5350",
  refused: "#90A4AE",
  upcoming: "#E8E6E0",
  "unable to take": "#FFD600",
  "out of medication": "#1a1a1a",
  discontinued: "#8D6E63",
  prn: "#AB47BC",
};

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "—";

const buildMonthDates = (monthValue = []) => {
  if (!monthValue?.length) return [];
  const start = moment(monthValue[0]).startOf("month");
  const end = moment(monthValue[monthValue.length - 1]).endOf("month");
  const dates = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, "day")) {
    dates.push(cursor.clone());
    cursor.add(1, "day");
  }
  return dates;
};

const buildCustomScheduleLabel = (form3 = {}) => {
  if (form3?.scheduleType !== "custom") return "";

  const repeatEvery = form3?.repeatEvery || "1";
  const repeatType = form3?.repeatType || "";

  if (repeatType === "week") {
    const weeklyCount = Array.isArray(form3?.repeatOn)
      ? form3.repeatOn.filter(Boolean).length
      : Number(repeatEvery) || 1;
    return `${weeklyCount}X per week`;
  }

  if (repeatType === "day") {
    return repeatEvery === "1" ? "Daily" : `${repeatEvery}X per day`;
  }

  if (repeatType === "month") {
    return `${repeatEvery}X per month`;
  }

  return "";
};

const buildAdministrationUnitLabel = (form3 = {}) => {
  const doseForm = `${form3?.dosageForm || ""}`.trim();
  if (!doseForm) return "Tab";

  const normalized = doseForm.toLowerCase();
  if (normalized.endsWith("s")) {
    return doseForm.slice(0, -1);
  }

  return doseForm;
};

const resolveFacilityName = (client) =>
  client?.facility?.business?.companyName ||
  client?.business?.companyName ||
  client?.company?.companyName ||
  client?.companyName ||
  client?.facility?.companyName ||
  client?.facility?.name ||
  client?.form1?.facility ||
  client?.facilityName ||
  "Facility";

const useStyles = createStyles((theme) => ({
  wrapper: {
    display: "flex",
  },
  content: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
  },
  btn: {
    marginLeft: "auto",
    marginTop: 50,
  },
  mymodal: {
    width: 818,
    overflow: "visible",
    "& .mantine-Modal-content": {
      height: "600px !important",
    },
    "& .mantine-Modal-inner": {
      padding: "0px !important",
    },
  },
  mwd: {
    width: "100%",
  },
  reportInputFields: {
    margin: "auto",
    marginBottom: 10,
    borderBottomColor: "#FFBA4599",
    borderBottomWidth: 2,
    borderBottomStyle: "solid",
    paddingBottom: 20,
    display: "flex",
    flexDirection: "row",
  },
  viewBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
}));

const AddMarReportModal = ({
  opened,
  onclose,
  title,
  userId,
  selectedClient,
  onReportClose,
}) => {
  const { classes } = useStyles();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [marViewModalOpened, setMarViewModalOpened] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [marDataFetched, setMarDataFetched] = useState(false);
  const { mar, loading } = useSelector((state) => state?.mar);
  const { profile } = useSelector((state) => state?.profile);
  const exportRootRef = useRef(null);
  const pdfExportRef = useRef(null);

  const form = useForm({
    initialValues: {
      // reportName: "",
      monthValue: [],
      selectedClient: null,
    },
    validate: {
      monthValue: (value) =>
        value.length > 0 ? null : "Please select at least one month.",
    },
  });

  const handleReportClick = (value) => {
    const validationResult = form.validate();
    if (validationResult.hasErrors) {
      return;
    }

    setMarViewModalOpened(true);
  };

  const handleMarViewModalClose = () => {
    setMarViewModalOpened(false);
    if (onReportClose) onReportClose();
  };

  const handleClose = () => {
    form.reset();
    onclose();
    if (onReportClose) onReportClose();
  };

  useEffect(() => {
    if (userId && opened) {
      form.setFieldValue("selectedClient", userId);
    }
  }, [userId, opened]);

  useEffect(() => {
    if (
      !marViewModalOpened ||
      !selectedClient?.id ||
      !form.values.monthValue?.length
    )
      return;

    const monthValue = form.values.monthValue;
    const startDate = monthValue[0];
    const endDate = monthValue[monthValue.length - 1] || monthValue[0];

    setMarDataFetched(false);

    const fetchReport = async () => {
      const startStr = moment(startDate).startOf("month").format("YYYY-MM-DD");
      const endStr = moment(endDate || startDate).endOf("month").format("YYYY-MM-DD");
      await getMar(`monthly_${startStr}_${endStr}`, selectedClient.id, true);
      setMarDataFetched(true);
    };

    fetchReport();
  }, [marViewModalOpened, selectedClient?.id, form.values.monthValue]);

  const monthDates = buildMonthDates(form.values.monthValue || []);
  const facilityName =
    profile?.business?.companyName ||
    profile?.companyName ||
    resolveFacilityName(selectedClient);
  const allergies = Array.isArray(selectedClient?.form1?.knownAllergies)
    ? selectedClient.form1.knownAllergies.filter(Boolean).join(", ") || "None"
    : "None";
  // Mirrors MarTab's flattenedAppointments — expands each MAR doc into flat per-entry rows.
  // PRN: one row per administration with chronological medicineRemaining.
  // Scheduled: one row per medicationType entry (each has its own date + timeSlot string).
  const expandedMar = React.useMemo(() => {
    if (!mar?.length) return [];

    const isRecordPRN = (r) => {
      if (r?.marType === "PRN") return true;
      const formMt = r?.form3?.medicationType;
      if (typeof formMt === "string" && formMt.toUpperCase() === "PRN") return true;
      if (Array.isArray(formMt) && formMt.length > 0) {
        const last = formMt[formMt.length - 1];
        const s = typeof last === "string" ? last : last?.status;
        if (typeof s === "string" && s.toUpperCase() === "PRN") return true;
      }
      return false;
    };

    // Pre-compute per-PRN-medication running medicineRemaining for chronological counts.
    const prnRemainingMap = {};
    (() => {
      const byMedId = {};
      mar.forEach((r) => {
        if (!isRecordPRN(r) || !Array.isArray(r.medicationType)) return;
        const mId = Number(r.medicationId);
        if (!byMedId[mId]) byMedId[mId] = { entries: [], outer: r.medicineRemaining };
        r.medicationType.forEach((entry) => {
          if (entry?.status !== "PRN") return;
          const em = moment(entry.date);
          if (!em.isValid()) return;
          byMedId[mId].entries.push({
            dateStr: em.format("YYYY-MM-DD"),
            slotMins: em.hours() * 60 + em.minutes(),
            dosage: Number(entry.dosage || 0),
          });
        });
      });
      Object.entries(byMedId).forEach(([mIdStr, { entries, outer }]) => {
        if (outer === "unlimited" || isNaN(Number(outer))) return;
        entries.sort((a, b) =>
          a.dateStr !== b.dateStr ? (a.dateStr < b.dateStr ? -1 : 1) : a.slotMins - b.slotMins
        );
        let running = Number(outer);
        entries.forEach((e) => {
          running -= e.dosage;
          prnRemainingMap[`${mIdStr}_${e.dateStr}_${e.slotMins}`] = String(running);
        });
      });
    })();

    return mar.flatMap((r) => {
      if (isRecordPRN(r)) {
        if (!Array.isArray(r.medicationType) || r.medicationType.length === 0) return [];
        return r.medicationType
          .filter((entry) => entry?.status === "PRN")
          .map((entry) => {
            const em = moment(entry.date);
            const dateStr = em.isValid() ? em.format("YYYY-MM-DD") : "";
            const slotMins = em.isValid() ? em.hours() * 60 + em.minutes() : 0;
            const computedRemaining =
              prnRemainingMap[`${Number(r.medicationId)}_${dateStr}_${slotMins}`];
            return {
              ...r,
              date: entry.date,
              timeSlot: null,
              medicationType: "PRN",
              medicineRemaining: computedRemaining ?? r.medicineRemaining,
              staff: entry.staff || r.staff,
              comments: entry.comments || r.comments,
              reason: entry.reason || r.reason || null,
              otherReason: entry.otherReason || r.otherReason || null,
              administeredAt: entry.administeredAt || null,
              dosage: entry.dosage ?? r.dosage,
            };
          });
      }
      // Scheduled: expand each medicationType entry to its own flat row.
      if (!Array.isArray(r.medicationType) || r.medicationType.length === 0) {
        return [{
          ...r,
          medicationType: "upcoming",
          timeSlot: getActiveSlotForDate(r.timeSlot, r.date),
        }];
      }
      return r.medicationType.map((mtEntry) => ({
        ...r,
        date: mtEntry.date,
        timeSlot: mtEntry.timeSlot || getActiveSlotForDate(r.timeSlot, mtEntry.date),
        medicationType: mtEntry.status || "upcoming",
        staff: mtEntry.staff || r.staff,
        comments: mtEntry.comments || r.comments,
        administeredAt: mtEntry.administeredAt || null,
        reason: mtEntry.reason || r.reason || null,
        otherReason: mtEntry.otherReason || r.otherReason || null,
        dosage: mtEntry.dosage ?? r.dosage,
      }));
    });
  }, [mar]);

  // Mirrors MarTab's virtual entry generation — uses buildSchedulePeriods + scheduleVersions
  // so historical schedule changes, timeslot versions, and medicine count versions all work.
  const allEntries = React.useMemo(() => {
    if (!monthDates.length) return [...expandedMar];

    const today = moment().startOf("day");
    const viewStart = monthDates[0].clone().startOf("day");
    const viewEnd = monthDates[monthDates.length - 1].clone().endOf("day");
    const _dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

    const normalizeSlotLocal = (slot) =>
      slot !== null && typeof slot === "object" ? slot.time : slot;

    const getActiveSlotsLocal = (timeSlotArr, dateObj) => {
      if (!Array.isArray(timeSlotArr)) return [];
      return timeSlotArr
        .filter((slot) => {
          const slotTime = slot !== null && typeof slot === "object" ? slot.time : slot;
          if (!slotTime) return false;
          if (typeof slot === "string") return true;
          const from = slot.effectiveFrom ? moment(slot.effectiveFrom).startOf("day") : null;
          const to = slot.effectiveTo ? moment(slot.effectiveTo).startOf("day") : null;
          if (from && from.isAfter(dateObj, "day")) return false;
          if (to && to.isBefore(dateObj, "day")) return false;
          return true;
        })
        .map((s) => (s !== null && typeof s === "object" ? s.time : s));
    };

    // Returns true if the slot's end time has already passed today.
    // Parses "6AM - 10AM" style strings; handles overnight slots like "10PM - 2AM".
    const isSlotPassedToday = (slotStr) => {
      if (!slotStr) return false;
      const parts = slotStr.split(" - ");
      if (parts.length < 2) return true;
      const endMoment = moment(parts[1].trim(), "hA");
      if (!endMoment.isValid()) return true;
      const startMoment = moment(parts[0].trim(), "hA");
      const nowMinutes = moment().hours() * 60 + moment().minutes();
      const endMinutes = endMoment.hours() * 60 + endMoment.minutes();
      const startMinutes = startMoment.isValid() ? startMoment.hours() * 60 + startMoment.minutes() : 0;
      if (endMinutes < startMinutes) {
        // Overnight slot (e.g. 10PM - 2AM): "passed" during the daytime gap between end and start
        return nowMinutes >= endMinutes && nowMinutes < startMinutes;
      }
      return nowMinutes >= endMinutes;
    };

    const isSentinelEndDate = (endDate) => {
      if (!endDate) return false;
      const d = new Date(endDate);
      if (isNaN(d.getTime())) return false;
      const isDecember31UTC = d.getUTCMonth() === 11 && d.getUTCDate() === 31;
      const isEndOfDaySeconds = d.getUTCSeconds() === 59 && d.getUTCMilliseconds() === 999;
      const isJan1UTC = d.getUTCMonth() === 0 && d.getUTCDate() === 1;
      const isEndOfPrevYearLocal = isJan1UTC && d.getUTCHours() < 5 && isEndOfDaySeconds;
      return (isDecember31UTC && isEndOfDaySeconds) || isEndOfPrevYearLocal;
    };

    // Enrich form3 medications with latest scheduled MAR doc metadata so scheduleVersions,
    // medicineCountVersions, and rawTimeSlot are always authoritative (not stale form3 cache).
    let medications = [...(selectedClient?.form3 || [])];
    if (mar?.length) {
      const latestMarByMedId = new Map();
      mar.forEach((doc) => {
        if (doc?.marType === "PRN") return;
        const id = String(doc.medicationId || "");
        if (!id) return;
        const cur = latestMarByMedId.get(id);
        if (!cur || new Date(doc.date) > new Date(cur.date)) latestMarByMedId.set(id, doc);
      });
      medications = medications.map((med) => {
        const marDoc = latestMarByMedId.get(String(med.id || ""));
        if (!marDoc) return med;
        return {
          ...med,
          rawTimeSlot: Array.isArray(marDoc.timeSlot) && marDoc.timeSlot.length > 0
            ? marDoc.timeSlot : null,
          ...(marDoc.scheduleType != null && { scheduleType: marDoc.scheduleType }),
          ...(marDoc.repeatType !== undefined && { repeatType: marDoc.repeatType }),
          ...(marDoc.repeatEvery != null && { repeatEvery: marDoc.repeatEvery }),
          ...(Array.isArray(marDoc.repeatOn) && { repeatOn: marDoc.repeatOn }),
          ...(marDoc.medicationDate && { medicationDate: marDoc.medicationDate }),
          ...(marDoc.medicationEndDate !== undefined && { medicationEndDate: marDoc.medicationEndDate }),
          scheduleVersions: marDoc.scheduleVersions || med.scheduleVersions || [],
          scheduleEffectiveFrom: marDoc.scheduleEffectiveFrom || med.scheduleEffectiveFrom || null,
          medicineCountVersions: marDoc.medicineCountVersions || med.medicineCountVersions || [],
          medicineCountEffectiveFrom: marDoc.medicineCountEffectiveFrom || med.medicineCountEffectiveFrom || null,
        };
      });
    }

    // Builds ordered schedule periods from scheduleVersions history + current active period.
    const buildSchedulePeriods = (med, medStart) => {
      const periods = [];
      if (Array.isArray(med.scheduleVersions) && med.scheduleVersions.length > 0) {
        med.scheduleVersions.forEach((version) => {
          if (!version.effectiveFrom) return;
          const anchor = moment(version.effectiveFrom).startOf("day");
          periods.push({
            from: anchor.clone(),
            to: version.effectiveTo ? moment(version.effectiveTo).endOf("day") : null,
            scheduleType: version.scheduleType || "daily",
            repeatType: version.repeatType || null,
            repeatEvery: Number(version.repeatEvery) || 1,
            repeatOn: Array.isArray(version.repeatOn) ? version.repeatOn : [],
            anchor,
          });
        });
      }
      const currentFrom = med.scheduleEffectiveFrom
        ? moment(med.scheduleEffectiveFrom).startOf("day")
        : medStart.clone();
      periods.push({
        from: currentFrom,
        to: null,
        scheduleType: med.scheduleType || "daily",
        repeatType: med.repeatType || null,
        repeatEvery: Number(med.repeatEvery) || 1,
        repeatOn: Array.isArray(med.repeatOn) ? med.repeatOn : [],
        anchor: currentFrom.clone(),
      });
      return periods;
    };

    // existingKeys from real expanded entries so virtual entries don't duplicate them.
    const existingKeys = new Set();
    expandedMar.forEach((entry) => {
      const dateStr = moment(entry?.date).format("YYYY-MM-DD");
      const slot = normalizeSlotLocal(entry?.timeSlot);
      const medId = String(entry?.medicationId || entry?.form3?.id || "");
      if (slot && medId) existingKeys.add(`${medId}_${dateStr}_${slot}`);
    });

    const virtual = [];

    medications.forEach((med) => {
      const medTypeRaw = med.medicationType;
      let isPRN = false;
      let isDiscontinued = !!med.discontinued;
      if (typeof medTypeRaw === "string") {
        isPRN = medTypeRaw.toUpperCase() === "PRN";
        isDiscontinued = isDiscontinued || medTypeRaw.toUpperCase() === "DISCONTINUED";
      } else if (Array.isArray(medTypeRaw) && medTypeRaw.length > 0) {
        const last = medTypeRaw[medTypeRaw.length - 1];
        if (last?.status) {
          isPRN = last.status.toUpperCase() === "PRN";
          isDiscontinued = isDiscontinued || last.status.toUpperCase() === "DISCONTINUED";
        }
      }
      if (isPRN) return;

      const medStart = moment(med.medicationDate).startOf("day");
      let medEnd =
        med.medicationEndDate && med.medicationEndDate !== "null" && !isSentinelEndDate(med.medicationEndDate)
          ? moment(med.medicationEndDate).endOf("day")
          : viewEnd.clone();

      let discontinuedCap = null;
      if (isDiscontinued) {
        const endOfYesterday = moment().subtract(1, "day").endOf("day");
        discontinuedCap = endOfYesterday;
        if (med.discontinuedOn) {
          const dayBefore = moment(med.discontinuedOn).subtract(1, "day").endOf("day");
          if (dayBefore.isBefore(endOfYesterday)) discontinuedCap = dayBefore;
        }
        // Don't cap medEnd — allow iterating future dates to generate "discontinued" upcoming slots
      }

      if (medEnd.isBefore(viewStart) || medStart.isAfter(viewEnd)) return;

      const iterStart = moment.max(viewStart.clone(), medStart.clone());
      const iterEnd = moment.min(viewEnd.clone(), medEnd.clone());

      const getSlotsForDate = (dateObj) => {
        if (med.rawTimeSlot) return getActiveSlotsLocal(med.rawTimeSlot, dateObj);
        if (Array.isArray(med.timeSlots) && med.timeSlots.length > 0) return med.timeSlots;
        if (med.timeSlot) {
          return Array.isArray(med.timeSlot)
            ? med.timeSlot.map(normalizeSlotLocal).filter(Boolean)
            : [med.timeSlot].filter(Boolean);
        }
        return [];
      };

      const schedulePeriods = buildSchedulePeriods(med, medStart);

      schedulePeriods.forEach((period) => {
        const periodStart = moment.max(period.from.clone(), iterStart.clone());
        const periodEnd = period.to
          ? moment.min(period.to.clone(), iterEnd.clone())
          : iterEnd.clone();
        if (periodStart.isAfter(periodEnd, "day")) return;

        const pType = period.scheduleType || "daily";
        const pAnchor = period.anchor;

        const generateVirtual = (dateObj, slot) => {
          const dateStr = dateObj.format("YYYY-MM-DD");
          const key = `${med.id}_${dateStr}_${slot}`;
          if (existingKeys.has(key)) return;
          const isPast = dateObj.isBefore(today, "day");
          const isToday = dateObj.isSame(today, "day");
          const isUpcomingSlot = (!isPast && !isToday) || (isToday && !isSlotPassedToday(slot));
          const isAfterDiscontinuedCap = discontinuedCap !== null && dateObj.isAfter(discontinuedCap, "day");

          existingKeys.add(key);
          const medicationType = isAfterDiscontinuedCap
            ? "discontinued"
            : isUpcomingSlot
            ? "upcoming"
            : "missed";
          virtual.push({
            _id: `virtual_${key}`,
            date: dateObj.toISOString(),
            timeSlot: slot,
            medicationId: med.id,
            medicationType,
            form3: med,
          });
        };

        if (pType === "daily") {
          let cur = periodStart.clone();
          while (cur.isSameOrBefore(periodEnd, "day")) {
            getSlotsForDate(cur).forEach((slot) => generateVirtual(cur.clone(), slot));
            cur.add(1, "day");
          }
        } else if (pType === "custom") {
          const pRepeatType = (period.repeatType || "day").toLowerCase();
          const pRepeatEvery = Number(period.repeatEvery) || 1;
          const pRepeatOn = Array.isArray(period.repeatOn) ? period.repeatOn : [];

          if (pRepeatType === "day") {
            let cur = pAnchor.clone();
            while (cur.isSameOrBefore(periodEnd, "day")) {
              if (cur.isSameOrAfter(periodStart, "day"))
                getSlotsForDate(cur).forEach((slot) => generateVirtual(cur.clone(), slot));
              cur.add(pRepeatEvery, "day");
            }
          } else if (pRepeatType === "week") {
            const effectiveRepeatOn = pRepeatOn.length > 0 ? pRepeatOn : [_dayNames[pAnchor.day()]];
            let weekCursor = pAnchor.clone().startOf("week");
            while (weekCursor.isSameOrBefore(periodEnd, "day")) {
              effectiveRepeatOn.forEach((dayName) => {
                const dayIndex = _dayNames.indexOf(dayName);
                if (dayIndex === -1) return;
                const target = weekCursor.clone().add(dayIndex, "days");
                if (
                  target.isSameOrAfter(periodStart, "day") &&
                  target.isSameOrBefore(periodEnd, "day") &&
                  target.isSameOrAfter(pAnchor, "day")
                )
                  getSlotsForDate(target).forEach((slot) => generateVirtual(target.clone(), slot));
              });
              weekCursor.add(pRepeatEvery, "week");
            }
          } else if (pRepeatType === "month") {
            let cur = pAnchor.clone();
            while (cur.isSameOrBefore(periodEnd, "day")) {
              if (cur.isSameOrAfter(periodStart, "day"))
                getSlotsForDate(cur).forEach((slot) => generateVirtual(cur.clone(), slot));
              cur.add(pRepeatEvery, "month");
            }
          }
        }
      });
    });

    // Recovery: inject historical Taken/Refused/Missed entries stored in medicationType[]
    // that fall within the view range but outside the medication's current medicationDate
    // (happens when a medication's start date is edited forward after entries were recorded).
    const recoveredEntries = [];
    (mar || []).forEach((r) => {
      if (r?.marType === "PRN") return;
      if (!Array.isArray(r.medicationType)) return;
      r.medicationType.forEach((entry) => {
        const status = entry?.status;
        if (!status || status === "upcoming") return;
        const entryDate = moment(entry.date);
        if (!entryDate.isValid()) return;
        if (entryDate.isBefore(viewStart) || entryDate.isAfter(viewEnd)) return;
        const dateStr = entryDate.format("YYYY-MM-DD");
        const entrySlot = entry.timeSlot;
        if (!entrySlot) return;
        const medId = String(r.medicationId || "");
        const key = `${medId}_${dateStr}_${entrySlot}`;
        if (existingKeys.has(key)) return;
        existingKeys.add(key);
        recoveredEntries.push({
          _id: r._id,
          date: `${dateStr}T12:00:00.000Z`,
          timeSlot: entrySlot,
          medicationId: r.medicationId,
          medicationType: status,
          form3: r.form3 || null,
          medicineRemaining: r.medicineRemaining,
          staff: entry.staff || r.staff || null,
          comments: entry.comments || r.comments || null,
          administeredAt: entry.administeredAt || null,
        });
      });
    });

    // Exclude real entries after discontinuation / medication end date.
    const discontinuationEndMap = new Map();
    medications.forEach((med) => {
      const cutoff =
        med.medicationEndDate && med.medicationEndDate !== "null" && !isSentinelEndDate(med.medicationEndDate)
          ? moment(med.medicationEndDate).endOf("day")
          : med.discontinuedOn
          ? moment(med.discontinuedOn).endOf("day")
          : null;
      if (cutoff) discontinuationEndMap.set(String(med.id), cutoff);
    });

    const filteredExpandedMar = expandedMar.filter((entry) => {
      const medId = String(entry?.medicationId || entry?.form3?.id || "");
      const cutoff = discontinuationEndMap.get(medId);
      if (!cutoff) return true;
      return moment(entry?.date).isSameOrBefore(cutoff, "day");
    });

    return [...virtual, ...filteredExpandedMar, ...recoveredEntries];
  }, [expandedMar, selectedClient, monthDates, mar]);

  const groupedMedications = React.useMemo(() => {
    const grouped = new Map();
    allEntries.forEach((entry) => {
      const medicationId =
        entry?.medicationId || entry?.form3?.id || entry?._id;
      const medicationName =
        entry?.form3?.medicationName || entry?.medicationName || "Medication";
      const statusStr = `${entry?.medicationType || ""}`.toLowerCase();
      const isPrn = statusStr === "prn";
      if (!grouped.has(medicationId)) {
        const form3 = entry?.form3 || {};
        const medicationName =
          form3?.medicationName || entry?.medicationName || "Medication";
        const strength = [form3?.dosage, form3?.unit, form3?.dosageForm]
          .filter(Boolean)
          .join(" ");
        const administrationUnit = buildAdministrationUnitLabel(form3);
        const quantity = form3?.medicationadministered
          ? `Take ${form3.medicationadministered} ${
              Number(form3.medicationadministered) > 1
                ? `${administrationUnit}s`
                : administrationUnit
            }`
          : "";
        const route = form3?.ingestionMode || "";
        const frequency = form3?.frequency || "";
        const customSchedule = buildCustomScheduleLabel(form3);
        const dosage = [
          strength,
          [quantity, route, frequency, customSchedule]
            .filter(Boolean)
            .join(" "),
        ]
          .filter(Boolean)
          .join(" - ");
        grouped.set(medicationId, {
          medicationId,
          medicationName,
          dosage,
          isPrn,
          instruction: form3?.medicationInstruction || "",
          rowsBySlot: {},
        });
      }

      const slot = isPrn
        ? `${entry?.administeredAt ? moment(entry.administeredAt).format("hh:mm A") : "PRN"} · PRN`
        : entry?.timeSlot || "Unscheduled";
      if (!grouped.get(medicationId).rowsBySlot[slot]) {
        grouped.get(medicationId).rowsBySlot[slot] = {};
      }
      const dayKey = moment(entry?.date).format("YYYY-MM-DD");
      // Only overwrite a virtual entry with a real one, not the other way around.
      const isVirtual = String(entry?._id || "").startsWith("virtual_");
      const existing = grouped.get(medicationId).rowsBySlot[slot][dayKey];
      if (
        !existing ||
        (!isVirtual && String(existing?._id || "").startsWith("virtual_"))
      ) {
        grouped.get(medicationId).rowsBySlot[slot][dayKey] = entry;
      }
    });

    return Array.from(grouped.values())
      .map((med) => ({
        ...med,
        slots: Object.keys(med.rowsBySlot).sort((a, b) => {
          const aIndex = SLOT_ORDER.indexOf(a);
          const bIndex = SLOT_ORDER.indexOf(b);

          if (med.isPrn) {
            const aMoment = moment(a.replace(" · PRN", ""), "hh:mm A", true);
            const bMoment = moment(b.replace(" · PRN", ""), "hh:mm A", true);
            if (aMoment.isValid() && bMoment.isValid()) {
              return aMoment.valueOf() - bMoment.valueOf();
            }
          }

          const normalizedA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
          const normalizedB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
          return normalizedA - normalizedB || a.localeCompare(b);
        }),
      }))
      .sort((a, b) => {
        if (a.isPrn !== b.isPrn) {
          return a.isPrn ? 1 : -1;
        }
        return a.medicationName.localeCompare(b.medicationName);
      });
  }, [allEntries]);

  const commentRows = React.useMemo(() => {
    const monthValue = form.values.monthValue;
    if (!monthValue?.length) return [];
    const viewStart = moment(monthValue[0]).startOf("month");
    const viewEnd = moment(monthValue[monthValue.length - 1]).endOf("month");
    return expandedMar.filter((entry) => {
      if (`${entry?.medicationType || ""}`.toLowerCase() === "prn") return false;
      if (!entry?.comments) return false;
      const d = moment(entry?.date);
      return d.isValid() && d.isBetween(viewStart, viewEnd, "day", "[]");
    });
  }, [expandedMar, form.values.monthValue]);

  const prnRows = React.useMemo(() => {
    const monthValue = form.values.monthValue;
    if (!monthValue?.length) return [];
    const viewStart = moment(monthValue[0]).startOf("month");
    const viewEnd = moment(monthValue[monthValue.length - 1]).endOf("month");
    return expandedMar.filter((entry) => {
      if (`${entry?.medicationType || ""}`.toLowerCase() !== "prn") return false;
      const d = moment(entry?.date);
      return d.isValid() && d.isBetween(viewStart, viewEnd, "day", "[]");
    });
  }, [expandedMar, form.values.monthValue]);

  const contributingStaff = React.useMemo(() => {
    const monthValue = form.values.monthValue;
    const seen = new Map();
    const viewStart = monthValue?.length ? moment(monthValue[0]).startOf("month") : null;
    const viewEnd = monthValue?.length ? moment(monthValue[monthValue.length - 1]).endOf("month") : null;

    expandedMar.forEach((entry) => {
      const staff = entry?.staff;
      if (!staff?.name) return;
      if (viewStart && viewEnd) {
        const d = moment(entry?.date);
        if (!d.isValid() || !d.isBetween(viewStart, viewEnd, "day", "[]")) return;
      }
      const key = staff?.id || staff?._id || staff?.name;
      if (!seen.has(key)) {
        seen.set(key, {
          id: key,
          name: staff.name,
          initials: getInitials(staff.name),
        });
      }
    });

    return Array.from(seen.values());
  }, [expandedMar, form.values.monthValue]);

  // Group monthDates by calendar month — each group = one PDF page.
  const monthGroups = React.useMemo(() => {
    if (!monthDates.length) return [];
    const groups = [];
    let current = null;
    monthDates.forEach((date) => {
      const key = date.format("YYYY-MM");
      if (!current || current.key !== key) {
        current = { key, label: date.format("MMMM YYYY"), dates: [] };
        groups.push(current);
      }
      current.dates.push(date);
    });
    return groups;
  }, [monthDates]);

  // Flat list of { col index, medicationName, slot, rowsBySlot } for PDF column headers.
  const allMedSlotColumns = React.useMemo(
    () =>
      groupedMedications.flatMap((med) =>
        med.slots.map((slot) => ({
          medicationId: med.medicationId,
          medicationName: med.medicationName,
          slot,
          rowsBySlot: med.rowsBySlot,
        })),
      ),
    [groupedMedications],
  );

  const handleDownloadPdf = async () => {
    if (exporting) return;

    try {
      setExporting(true);

      // Build sorted months array from selected range
      const months = [];
      if (form.values.monthValue?.length) {
        const rangeStart = moment(form.values.monthValue[0]).startOf("month");
        const rangeEnd = moment(form.values.monthValue[form.values.monthValue.length - 1]).endOf("month");
        const cursor = rangeStart.clone();
        while (cursor.isSameOrBefore(rangeEnd, "month")) {
          months.push(cursor.clone());
          cursor.add(1, "month");
        }
      }
      if (!months.length) return;

      const pdf = new jsPDF("landscape", "mm", "a3");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;
      let isFirstPage = true;

      const captureAndAdd = async (container) => {
        document.body.appendChild(container);
        const canvas = await html2canvas(container, {
          scale: 1.4,
          useCORS: true,
          backgroundColor: "#ffffff",
          windowWidth: container.scrollWidth,
          windowHeight: container.scrollHeight,
        });
        document.body.removeChild(container);
        const imgData = canvas.toDataURL("image/png");
        const ratio = Math.min(usableWidth / canvas.width, usableHeight / canvas.height);
        const imgW = canvas.width * ratio;
        const imgH = canvas.height * ratio;
        const x = (pageWidth - imgW) / 2;
        if (!isFirstPage) pdf.addPage("a3", "landscape");
        isFirstPage = false;
        pdf.addImage(imgData, "PNG", x, margin, imgW, imgH);
      };

      const residentName = selectedClient?.name || "Unknown";
      const dob = selectedClient?.form1?.dob
        ? moment(selectedClient.form1.dob).format("MM/DD/YYYY")
        : "—";

      // Status legend HTML — same colors as modal
      const legendHtml = Object.entries(STATUS_COLORS)
        .map(
          ([status, color]) =>
            `<div style="display:flex;align-items:center;gap:8px;">
              <div style="width:12px;height:12px;border-radius:50%;background:${color};flex-shrink:0;${color === "#E8E6E0" ? "border:1px solid #d8d1c2;" : ""}"></div>
              <span style="font-size:12px;color:#5f5a50;text-transform:capitalize;">${status}</span>
            </div>`
        )
        .join("");

      // Contributing staff HTML — same layout as modal
      const staffPalette = [
        { bg: "#f9edd7", color: "#8a5613" },
        { bg: "#dff2ed", color: "#156c5a" },
        { bg: "#ecebfd", color: "#4f4abb" },
        { bg: "#e4f0fb", color: "#2d69ac" },
      ];
      const staffHtml = contributingStaff.length
        ? `<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:14px;">
            ${contributingStaff
              .map((staff, i) => {
                const p = staffPalette[i % staffPalette.length];
                return `<div style="border:1px solid #e4ddcf;border-radius:16px;padding:12px 16px;display:flex;align-items:center;gap:14px;">
                  <div style="width:48px;height:48px;border-radius:50%;background:${p.bg};color:${p.color};display:flex;align-items:center;justify-content:center;font-weight:800;font-size:18px;flex-shrink:0;">${staff.initials}</div>
                  <div>
                    <div style="font-weight:700;font-size:18px;color:#23211c;">${staff.name}</div>
                    <div style="font-size:14px;color:#6c675e;">Initial: ${staff.initials}</div>
                  </div>
                </div>`;
              })
              .join("")}
          </div>`
        : `<span style="font-size:14px;color:#8c8477;">No contributing staff were recorded for this month.</span>`;

      // --- One PDF page per month (same table structure as modal) ---
      for (const monthMoment of months) {
        const daysInMonth = monthMoment.daysInMonth();
        const datesForMonth = Array.from({ length: daysInMonth }, (_, i) =>
          monthMoment.clone().date(i + 1)
        );
        const firstDay = datesForMonth[0].format("MMMM D, YYYY");
        const lastDay = datesForMonth[datesForMonth.length - 1].format("MMMM D, YYYY");

        // Date column headers — same as modal
        const dateThs = datesForMonth
          .map(
            (date) =>
              `<th style="padding:10px 6px;border-bottom:1px solid #e8dfd0;border-right:1px solid #e8dfd0;text-align:center;min-width:44px;">
                <div style="font-size:10px;color:#9b9488;">${date.format("ddd")}</div>
                <div style="font-weight:700;font-size:13px;color:#23211c;">${date.format("D")}</div>
              </th>`
          )
          .join("");

        // Table rows — medications as rows, dates as columns (same as modal)
        const bodyRows = groupedMedications.length
          ? groupedMedications
              .flatMap((medication) =>
                medication.slots.map((slot, index) => {
                  const medCell =
                    index === 0
                      ? `<td rowspan="${medication.slots.length}" style="padding:14px;border-bottom:1px solid #eee6d9;border-right:1px solid #e8dfd0;vertical-align:top;background:#fffdfa;">
                          <div style="font-weight:700;font-size:13px;color:#23211c;">${medication.medicationName}</div>
                          <div style="margin-top:3px;font-size:11px;font-weight:600;color:#5c5549;">${medication.dosage || "—"}</div>
                          ${medication.isPrn && medication.instruction ? `<div style="margin-top:4px;font-size:11px;color:#8c8477;">${medication.instruction}</div>` : ""}
                        </td>`
                      : "";

                  const dateCells = datesForMonth
                    .map((date) => {
                      const dayKey = date.format("YYYY-MM-DD");
                      const entry = medication.rowsBySlot?.[slot]?.[dayKey];
                      const status = `${entry?.medicationType || "upcoming"}`.toLowerCase();
                      const color = STATUS_COLORS[status] || STATUS_COLORS.upcoming;
                      const shouldShowInitials =
                        !!entry?.staff?.name &&
                        status !== "missed" &&
                        status !== "upcoming";
                      const cellText = shouldShowInitials
                        ? getInitials(entry.staff.name)
                        : entry
                        ? "•"
                        : "";
                      const bgColor = status === "upcoming" ? "#fff" : `${color}18`;
                      const innerBg = status === "upcoming" ? "transparent" : `${color}22`;
                      const textColor = color === "#1a1a1a" ? "#fff" : "#23211c";
                      return `<td style="border-bottom:1px solid #eee6d9;border-right:1px solid #e8dfd0;text-align:center;padding:0;background:${bgColor};">
                        <div style="min-height:42px;display:flex;align-items:center;justify-content:center;color:${textColor};font-weight:700;background:${innerBg};">${cellText}</div>
                      </td>`;
                    })
                    .join("");

                  return `<tr>
                    ${medCell}
                    <td style="padding:12px 14px;border-bottom:1px solid #eee6d9;border-right:1px solid #e8dfd0;color:#4c473f;font-weight:600;background:#fff;">${slot}</td>
                    ${dateCells}
                  </tr>`;
                })
              )
              .join("")
          : `<tr><td colspan="${datesForMonth.length + 2}" style="padding:28px;text-align:center;color:#8c8477;">No MAR entries found for ${monthMoment.format("MMMM YYYY")}.</td></tr>`;

        const container = document.createElement("div");
        container.style.cssText =
          "position:fixed;left:-200000px;top:0;background:#f5f3ee;padding:20px;font-family:Inter,Arial,sans-serif;";

        container.innerHTML = `
          <div style="background:#fff;border-radius:18px;border:1px solid #e6dfd2;overflow:hidden;">

            <div style="padding:18px 22px 16px;border-bottom:1px solid #eee6d9;display:flex;justify-content:space-between;gap:20px;align-items:flex-start;">
              <div>
                <div style="font-weight:800;font-size:28px;color:#23211c;">Medication Administration Record</div>
                <div style="margin-top:4px;font-size:14px;color:#7a7468;">${facilityName}</div>
              </div>
              <div style="text-align:right;">
                <div style="font-weight:700;font-size:14px;color:#23211c;">Report Month</div>
                <div style="font-size:14px;color:#7a7468;">${firstDay} - ${lastDay}</div>
              </div>
            </div>

            <div style="padding:14px 22px;border-bottom:1px solid #eee6d9;display:flex;gap:12px;flex-wrap:wrap;align-items:center;">
              <div style="padding:6px 12px;border-radius:999px;background:#f7f1df;font-size:12px;font-weight:700;color:#7a5d10;">${groupedMedications.length} medications</div>
              <div style="padding:6px 12px;border-radius:999px;background:#f0f4fb;font-size:12px;font-weight:700;color:#405b85;">${commentRows.length} comments</div>
              <div style="padding:6px 12px;border-radius:999px;background:#f7eef9;font-size:12px;font-weight:700;color:#7a3d86;">${prnRows.length} PRN administrations</div>
            </div>

            <div style="background:#242c39;color:#fff;padding:16px 22px;display:flex;justify-content:space-between;gap:18px;flex-wrap:wrap;">
              <div>
                <div style="font-size:11px;text-transform:uppercase;color:#b9c2cf;letter-spacing:0.8px;">Resident</div>
                <div style="font-weight:700;font-size:18px;">${residentName}</div>
              </div>
              <div>
                <div style="font-size:11px;text-transform:uppercase;color:#b9c2cf;letter-spacing:0.8px;">DOB</div>
                <div style="font-weight:600;font-size:15px;">${dob}</div>
              </div>
              <div style="max-width:340px;">
                <div style="font-size:11px;text-transform:uppercase;color:#b9c2cf;letter-spacing:0.8px;">Allergies</div>
                <div style="font-weight:600;font-size:15px;">${allergies}</div>
              </div>
            </div>

            <div style="padding:18px 22px;border-bottom:1px solid #eee6d9;">
              <div style="font-weight:800;font-size:14px;text-transform:uppercase;color:#8b8478;margin-bottom:14px;letter-spacing:1.1px;">Contributing Staff</div>
              ${staffHtml}
            </div>

            <div style="padding:14px 22px;border-bottom:1px solid #eee6d9;display:flex;flex-wrap:wrap;gap:12px;">
              ${legendHtml}
            </div>

            <div>
              <table style="width:100%;border-collapse:collapse;font-size:12px;min-width:1200px;">
                <thead>
                  <tr style="background:#f8f4ec;">
                    <th style="padding:12px 14px;border-bottom:1px solid #e8dfd0;border-right:1px solid #e8dfd0;text-align:left;min-width:260px;">Medication</th>
                    <th style="padding:12px 14px;border-bottom:1px solid #e8dfd0;border-right:1px solid #e8dfd0;text-align:left;min-width:120px;">Time Slot</th>
                    ${dateThs}
                  </tr>
                </thead>
                <tbody>${bodyRows}</tbody>
              </table>
            </div>

          </div>
        `;

        await captureAndAdd(container);
      }

      // --- Comments page (same structure as modal) ---
      {
        const commentTrs = commentRows.length
          ? commentRows
              .map(
                (entry) =>
                  `<tr>
                    <td style="padding:12px 14px;border-bottom:1px solid #eee6d9;border-right:1px solid #e8dfd0;">${entry?.administeredAt || entry?.date ? moment(entry.administeredAt || entry.date).format("MM/DD/YYYY") : "—"}</td>
                    <td style="padding:12px 14px;border-bottom:1px solid #eee6d9;border-right:1px solid #e8dfd0;">${entry?.administeredAt ? moment(entry.administeredAt).format("hh:mm A") : "—"}</td>
                    <td style="padding:12px 14px;border-bottom:1px solid #eee6d9;border-right:1px solid #e8dfd0;">${entry?.form3?.medicationName || "Medication"}</td>
                    <td style="padding:12px 14px;border-bottom:1px solid #eee6d9;border-right:1px solid #e8dfd0;text-transform:capitalize;">${entry?.medicationType || "recorded"}</td>
                    <td style="padding:12px 14px;border-bottom:1px solid #eee6d9;border-right:1px solid #e8dfd0;">${entry?.comments || "—"}</td>
                    <td style="padding:12px 14px;border-bottom:1px solid #eee6d9;">${entry?.staff?.name || "—"}</td>
                  </tr>`
              )
              .join("")
          : `<tr><td colspan="6" style="padding:24px;text-align:center;color:#8c8477;">No medication comments were recorded for this period.</td></tr>`;

        const commentsContainer = document.createElement("div");
        commentsContainer.style.cssText =
          "position:fixed;left:-200000px;top:0;background:#f5f3ee;padding:20px;font-family:Inter,Arial,sans-serif;";
        commentsContainer.innerHTML = `
          <div style="background:#fff;border-radius:18px;border:1px solid #e6dfd2;overflow:hidden;">
            <div style="padding:18px 22px;border-bottom:1px solid #eee6d9;">
              <div style="font-weight:800;font-size:24px;color:#23211c;">MAR Administration Comments</div>
            </div>
            <table style="width:100%;border-collapse:collapse;font-size:12px;min-width:900px;">
              <thead>
                <tr style="background:#f8f4ec;">
                  ${["Date", "Time", "Medication", "Status", "Comment", "Staff"]
                    .map((l) => `<th style="padding:12px 14px;border-bottom:1px solid #e8dfd0;border-right:1px solid #e8dfd0;text-align:left;">${l}</th>`)
                    .join("")}
                </tr>
              </thead>
              <tbody>${commentTrs}</tbody>
            </table>
          </div>
        `;
        await captureAndAdd(commentsContainer);
      }

      // --- PRN page (same structure as modal) ---
      if (prnRows.length > 0) {
        const prnTrs = prnRows
          .map(
            (entry) =>
              `<tr>
                <td style="padding:12px 14px;border-bottom:1px solid #eee6d9;border-right:1px solid #e8dfd0;">${entry?.date ? moment(entry.date).format("MM/DD/YYYY") : "—"}</td>
                <td style="padding:12px 14px;border-bottom:1px solid #eee6d9;border-right:1px solid #e8dfd0;">${entry?.administeredAt ? moment(entry.administeredAt).format("hh:mm A") : "—"}</td>
                <td style="padding:12px 14px;border-bottom:1px solid #eee6d9;border-right:1px solid #e8dfd0;">${entry?.form3?.medicationName || "Medication"}</td>
                <td style="padding:12px 14px;border-bottom:1px solid #eee6d9;border-right:1px solid #e8dfd0;">${entry?.reason || entry?.otherReason || "—"}</td>
                <td style="padding:12px 14px;border-bottom:1px solid #eee6d9;border-right:1px solid #e8dfd0;">${entry?.comments || "—"}</td>
                <td style="padding:12px 14px;border-bottom:1px solid #eee6d9;">${entry?.staff?.name || "—"}</td>
              </tr>`
          )
          .join("");

        const prnContainer = document.createElement("div");
        prnContainer.style.cssText =
          "position:fixed;left:-200000px;top:0;background:#f5f3ee;padding:20px;font-family:Inter,Arial,sans-serif;";
        prnContainer.innerHTML = `
          <div style="background:#fff;border-radius:18px;border:1px solid #e6dfd2;overflow:hidden;">
            <div style="padding:18px 22px;border-bottom:1px solid #eee6d9;">
              <div style="font-weight:800;font-size:24px;color:#23211c;">PRN Medication Administration</div>
            </div>
            <table style="width:100%;border-collapse:collapse;font-size:12px;min-width:900px;">
              <thead>
                <tr style="background:#f8f4ec;">
                  ${["Date", "Time", "Medication", "Reason", "Comment", "Staff"]
                    .map((l) => `<th style="padding:12px 14px;border-bottom:1px solid #e8dfd0;border-right:1px solid #e8dfd0;text-align:left;">${l}</th>`)
                    .join("")}
                </tr>
              </thead>
              <tbody>${prnTrs}</tbody>
            </table>
          </div>
        `;
        await captureAndAdd(prnContainer);
      }

      const startLabel = months[0]?.format("MMM-YYYY") || "report";
      const endLabel = months[months.length - 1]?.format("MMM-YYYY");
      const fileLabel = months.length > 1 ? `${startLabel}-to-${endLabel}` : startLabel;
      pdf.save(`${residentName}-mar-${fileLabel}.pdf`);
    } catch (error) {
      console.error("Unable to export MAR report", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Modal
        centered
        size={600}
        shadow="md"
        radius={"lg"}
        zIndex={1000}
        opened={opened}
        onClose={handleClose}
        overlayProps={{ blur: 3 }}
        className={classes.mymodal}
        title={
          <Group style={{ zIndex: -1 }}>
            <img src="/assets/images/addfac.png" alt="" />
            <Text ff={"Inter"} fw={600} fz={22}>
              {title}
            </Text>
          </Group>
        }
      >
        <form onSubmit={form.onSubmit(handleReportClick)}>
          <Box
            style={{
              border: "1px solid #E0E0E0",
              borderRadius: "5px",
              padding: "10px",
              marginTop: "10px",
            }}
          >
            {selectedClient?.name}
          </Box>
          <MonthPickerInput
            mt={10}
            size="md"
            clearable
            type="range"
            allowSingleDateInRange
            label="Select Months"
            placeholder="Select Months"
            {...form.getInputProps("monthValue")}
          />

          <Box className={classes.viewBtn}>
            <Button color="yellow" c="#000" mt={20} size="md" type="submit">
              View Report
            </Button>
          </Box>
        </form>
      </Modal>
      {marViewModalOpened ? (
        <Modal
          centered
          size="92%"
          shadow="md"
          radius="lg"
          zIndex={1001}
          opened={marViewModalOpened}
          onClose={handleMarViewModalClose}
          overlayProps={{ blur: 3 }}
          styles={{
            body: {
              padding: 0,
            },
          }}
          title="MAR Report"
        >
          <Box
            ref={exportRootRef}
            style={{
              background: "#f5f3ee",
              padding: 20,
              maxHeight: "82vh",
              overflowY: "auto",
            }}
          >
            {!marDataFetched || loading ? (
              <Box
                style={{
                  minHeight: 320,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Loader color="yellow" />
              </Box>
            ) : (
              <>
                <Box
                  className="mar-export-page"
                  style={{
                    background: "#fff",
                    borderRadius: 18,
                    border: "1px solid #e6dfd2",
                    overflow: "hidden",
                    marginBottom: 18,
                  }}
                >
                  <Box
                    style={{
                      padding: isMobile ? "12px 14px 10px" : "18px 22px 16px",
                      borderBottom: "1px solid #eee6d9",
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      justifyContent: "space-between",
                      gap: isMobile ? 8 : 20,
                      alignItems: "flex-start",
                    }}
                  >
                    <Box>
                      <Text fw={800} fz={isMobile ? 18 : 28} c="#23211c">
                        Medication Administration Record
                      </Text>
                      <Text mt={4} fz={14} c="#7a7468">
                        {facilityName}
                      </Text>
                    </Box>
                    <Box style={{ textAlign: isMobile ? "left" : "right" }}>
                      <Text fw={700} fz={14} c="#23211c">
                        Report Month
                      </Text>
                      <Text fz={14} c="#7a7468">
                        {monthDates.length
                          ? `${monthDates[0].format("MMMM D, YYYY")} - ${monthDates[
                              monthDates.length - 1
                            ].format("MMMM D, YYYY")}`
                          : "No month selected"}
                      </Text>
                    </Box>
                  </Box>

                  <Box
                    style={{
                      padding: "14px 22px",
                      borderBottom: "1px solid #eee6d9",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <Group spacing={8}>
                      <Box
                        style={{
                          padding: "6px 12px",
                          borderRadius: 999,
                          background: "#f7f1df",
                        }}
                      >
                        <Text fz={12} fw={700} c="#7a5d10">
                          {groupedMedications.length} medications
                        </Text>
                      </Box>
                      <Box
                        style={{
                          padding: "6px 12px",
                          borderRadius: 999,
                          background: "#f0f4fb",
                        }}
                      >
                        <Text fz={12} fw={700} c="#405b85">
                          {commentRows.length} comments
                        </Text>
                      </Box>
                      <Box
                        style={{
                          padding: "6px 12px",
                          borderRadius: 999,
                          background: "#f7eef9",
                        }}
                      >
                        <Text fz={12} fw={700} c="#7a3d86">
                          {prnRows.length} PRN administrations
                        </Text>
                      </Box>
                    </Group>
                    <Button
                      data-export-hidden="true"
                      color="yellow"
                      c="#000"
                      radius="md"
                      loading={exporting}
                      disabled={!monthDates.length}
                      onClick={handleDownloadPdf}
                    >
                      Download PDF
                    </Button>
                  </Box>

                  <Box
                    style={{
                      background: "#242c39",
                      color: "#fff",
                      padding: "16px 22px",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 18,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box>
                      <Text fz={11} tt="uppercase" c="#b9c2cf">
                        Resident
                      </Text>
                      <Text fw={700} fz={18}>
                        {selectedClient?.name || "Unknown"}
                      </Text>
                    </Box>
                    <Box>
                      <Text fz={11} tt="uppercase" c="#b9c2cf">
                        DOB
                      </Text>
                      <Text fw={600} fz={15}>
                        {selectedClient?.form1?.dob
                          ? moment(selectedClient.form1.dob).format(
                              "MM/DD/YYYY",
                            )
                          : "—"}
                      </Text>
                    </Box>
                    <Box style={{ maxWidth: 340 }}>
                      <Text fz={11} tt="uppercase" c="#b9c2cf">
                        Allergies
                      </Text>
                      <Text fw={600} fz={15}>
                        {allergies}
                      </Text>
                    </Box>
                  </Box>

                  <Box
                    style={{
                      padding: "18px 22px",
                      borderBottom: "1px solid #eee6d9",
                    }}
                  >
                    <Text
                      fw={800}
                      fz={14}
                      tt="uppercase"
                      c="#8b8478"
                      mb={14}
                      letterSpacing={1.1}
                    >
                      Contributing Staff
                    </Text>
                    {contributingStaff.length ? (
                      <Box
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(220px, 1fr))",
                          gap: 14,
                        }}
                      >
                        {contributingStaff.map((staff, index) => {
                          const accentPalette = [
                            { background: "#f9edd7", color: "#8a5613" },
                            { background: "#dff2ed", color: "#156c5a" },
                            { background: "#ecebfd", color: "#4f4abb" },
                            { background: "#e4f0fb", color: "#2d69ac" },
                          ];
                          const accent =
                            accentPalette[index % accentPalette.length];

                          return (
                            <Box
                              key={staff.id}
                              style={{
                                border: "1px solid #e4ddcf",
                                borderRadius: 16,
                                padding: "12px 16px",
                                display: "flex",
                                alignItems: "center",
                                gap: 14,
                              }}
                            >
                              <Box
                                style={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: "50%",
                                  background: accent.background,
                                  color: accent.color,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: 800,
                                  fontSize: 18,
                                  flexShrink: 0,
                                }}
                              >
                                {staff.initials}
                              </Box>
                              <Box>
                                <Text fw={700} fz={18} c="#23211c">
                                  {staff.name}
                                </Text>
                                <Text fz={14} c="#6c675e">
                                  Initial: {staff.initials}
                                </Text>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Text fz={14} c="#8c8477">
                        No contributing staff were recorded for this month.
                      </Text>
                    )}
                  </Box>

                  <Box
                    style={{
                      padding: "14px 22px",
                      borderBottom: "1px solid #eee6d9",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 12,
                    }}
                  >
                    {Object.entries(STATUS_COLORS).map(([status, color]) => (
                      <Box
                        key={status}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Box
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 999,
                            background: color,
                            border:
                              color === "#E8E6E0"
                                ? "1px solid #d8d1c2"
                                : "none",
                          }}
                        />
                        <Text fz={12} c="#5f5a50" tt="capitalize">
                          {status}
                        </Text>
                      </Box>
                    ))}
                  </Box>

                  <Box style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        minWidth: 1200,
                        fontSize: 12,
                      }}
                    >
                      <thead>
                        <tr style={{ background: "#f8f4ec" }}>
                          <th
                            style={{
                              padding: "12px 14px",
                              borderBottom: "1px solid #e8dfd0",
                              borderRight: "1px solid #e8dfd0",
                              textAlign: "left",
                              minWidth: 260,
                            }}
                          >
                            Medication
                          </th>
                          <th
                            style={{
                              padding: "12px 14px",
                              borderBottom: "1px solid #e8dfd0",
                              borderRight: "1px solid #e8dfd0",
                              textAlign: "left",
                              minWidth: 120,
                            }}
                          >
                            Time Slot
                          </th>
                          {monthDates.map((date) => (
                            <th
                              key={date.format("YYYY-MM-DD")}
                              style={{
                                padding: "10px 6px",
                                borderBottom: "1px solid #e8dfd0",
                                borderRight: "1px solid #e8dfd0",
                                textAlign: "center",
                                minWidth: 44,
                              }}
                            >
                              <Text fz={10} c="#9b9488">
                                {date.format("ddd")}
                              </Text>
                              <Text fw={700} fz={13} c="#23211c">
                                {date.format("D")}
                              </Text>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {groupedMedications.length ? (
                          groupedMedications.flatMap((medication) =>
                            medication.slots.map((slot, index) => (
                              <tr key={`${medication.medicationId}-${slot}`}>
                                {index === 0 ? (
                                  <td
                                    rowSpan={medication.slots.length}
                                    style={{
                                      padding: "14px",
                                      borderBottom: "1px solid #eee6d9",
                                      borderRight: "1px solid #e8dfd0",
                                      verticalAlign: "top",
                                      background: "#fffdfa",
                                    }}
                                  >
                                    <Text fw={700} fz={13} c="#23211c">
                                      {medication.medicationName}
                                    </Text>
                                    <Text mt={3} fz={11} fw={600} c="#5c5549">
                                      {medication.dosage || "—"}
                                    </Text>
                                    {medication.isPrn &&
                                    medication.instruction ? (
                                      <Text mt={4} fz={11} c="#8c8477">
                                        {medication.instruction}
                                      </Text>
                                    ) : null}
                                  </td>
                                ) : null}
                                <td
                                  style={{
                                    padding: "12px 14px",
                                    borderBottom: "1px solid #eee6d9",
                                    borderRight: "1px solid #e8dfd0",
                                    color: "#4c473f",
                                    fontWeight: 600,
                                    background: "#fff",
                                  }}
                                >
                                  {slot}
                                </td>
                                {monthDates.map((date) => {
                                  const dayKey = date.format("YYYY-MM-DD");
                                  const entry =
                                    medication.rowsBySlot?.[slot]?.[dayKey];
                                  const status =
                                    `${entry?.medicationType || "upcoming"}`.toLowerCase();
                                  const color =
                                    STATUS_COLORS[status] ||
                                    STATUS_COLORS.upcoming;
                                  const shouldShowInitials =
                                    !!entry?.staff?.name &&
                                    status !== "missed" &&
                                    status !== "upcoming";
                                  return (
                                    <td
                                      key={`${medication.medicationId}-${slot}-${dayKey}`}
                                      style={{
                                        borderBottom: "1px solid #eee6d9",
                                        borderRight: "1px solid #e8dfd0",
                                        textAlign: "center",
                                        padding: 0,
                                        background:
                                          status === "upcoming"
                                            ? "#fff"
                                            : `${color}18`,
                                      }}
                                    >
                                      <Box
                                        style={{
                                          minHeight: 42,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          color:
                                            color === "#1a1a1a"
                                              ? "#fff"
                                              : "#23211c",
                                          fontWeight: 700,
                                          background:
                                            status === "upcoming"
                                              ? "transparent"
                                              : `${color}22`,
                                        }}
                                      >
                                        {shouldShowInitials
                                          ? getInitials(entry.staff.name)
                                          : entry
                                            ? "•"
                                            : ""}
                                      </Box>
                                    </td>
                                  );
                                })}
                              </tr>
                            )),
                          )
                        ) : (
                          <tr>
                            <td
                              colSpan={monthDates.length + 2}
                              style={{
                                padding: 28,
                                textAlign: "center",
                                color: "#8c8477",
                              }}
                            >
                              No MAR entries found for the selected month.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </Box>
                </Box>

                <Box
                  className="mar-export-page"
                  style={{
                    background: "#fff",
                    borderRadius: 18,
                    border: "1px solid #e6dfd2",
                    overflow: "hidden",
                    marginBottom: prnRows.length ? 18 : 0,
                  }}
                >
                  <Box
                    style={{
                      padding: "18px 22px",
                      borderBottom: "1px solid #eee6d9",
                    }}
                  >
                    <Text fw={800} fz={24} c="#23211c">
                      MAR Administration Comments
                    </Text>
                  </Box>
                  <Box style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        minWidth: 900,
                        fontSize: 12,
                      }}
                    >
                      <thead>
                        <tr style={{ background: "#f8f4ec" }}>
                          {[
                            "Date",
                            "Time",
                            "Medication",
                            "Status",
                            "Comment",
                            "Staff",
                          ].map((label) => (
                            <th
                              key={label}
                              style={{
                                padding: "12px 14px",
                                borderBottom: "1px solid #e8dfd0",
                                borderRight: "1px solid #e8dfd0",
                                textAlign: "left",
                              }}
                            >
                              {label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {commentRows.length ? (
                          commentRows.map((entry, index) => (
                            <tr
                              key={`${entry?._id || entry?.id || "comment"}-${index}`}
                            >
                              <td
                                style={{
                                  padding: "12px 14px",
                                  borderBottom: "1px solid #eee6d9",
                                  borderRight: "1px solid #e8dfd0",
                                }}
                              >
                                {entry?.administeredAt || entry?.date
                                  ? moment(entry.administeredAt || entry.date).format("MM/DD/YYYY")
                                  : "—"}
                              </td>
                              <td
                                style={{
                                  padding: "12px 14px",
                                  borderBottom: "1px solid #eee6d9",
                                  borderRight: "1px solid #e8dfd0",
                                }}
                              >
                                {entry?.administeredAt
                                  ? moment(entry.administeredAt).format("hh:mm A")
                                  : "—"}
                              </td>
                              <td
                                style={{
                                  padding: "12px 14px",
                                  borderBottom: "1px solid #eee6d9",
                                  borderRight: "1px solid #e8dfd0",
                                }}
                              >
                                {entry?.form3?.medicationName || "Medication"}
                              </td>
                              <td
                                style={{
                                  padding: "12px 14px",
                                  borderBottom: "1px solid #eee6d9",
                                  borderRight: "1px solid #e8dfd0",
                                  textTransform: "capitalize",
                                }}
                              >
                                {entry?.medicationType || "recorded"}
                              </td>
                              <td
                                style={{
                                  padding: "12px 14px",
                                  borderBottom: "1px solid #eee6d9",
                                  borderRight: "1px solid #e8dfd0",
                                }}
                              >
                                {entry?.comments || "—"}
                              </td>
                              <td
                                style={{
                                  padding: "12px 14px",
                                  borderBottom: "1px solid #eee6d9",
                                }}
                              >
                                {entry?.staff?.name || "—"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={6}
                              style={{
                                padding: 24,
                                textAlign: "center",
                                color: "#8c8477",
                              }}
                            >
                              No medication comments were recorded for this
                              month.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </Box>
                </Box>

                {prnRows.length ? (
                  <Box
                    className="mar-export-page"
                    style={{
                      background: "#fff",
                      borderRadius: 18,
                      border: "1px solid #e6dfd2",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      style={{
                        padding: "18px 22px",
                        borderBottom: "1px solid #eee6d9",
                      }}
                    >
                      <Text fw={800} fz={24} c="#23211c">
                        PRN Medication Administration
                      </Text>
                    </Box>
                    <Box style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          minWidth: 900,
                          fontSize: 12,
                        }}
                      >
                        <thead>
                          <tr style={{ background: "#f8f4ec" }}>
                            {[
                              "Date",
                              "Time",
                              "Medication",
                              "Reason",
                              "Comment",
                              "Staff",
                            ].map((label) => (
                              <th
                                key={label}
                                style={{
                                  padding: "12px 14px",
                                  borderBottom: "1px solid #e8dfd0",
                                  borderRight: "1px solid #e8dfd0",
                                  textAlign: "left",
                                }}
                              >
                                {label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {prnRows.map((entry, index) => (
                            <tr
                              key={`${entry?._id || entry?.id || "prn"}-${index}`}
                            >
                              <td
                                style={{
                                  padding: "12px 14px",
                                  borderBottom: "1px solid #eee6d9",
                                  borderRight: "1px solid #e8dfd0",
                                }}
                              >
                                {entry?.administeredAt || entry?.date
                                  ? moment(entry.administeredAt || entry.date).format("MM/DD/YYYY")
                                  : "—"}
                              </td>
                              <td
                                style={{
                                  padding: "12px 14px",
                                  borderBottom: "1px solid #eee6d9",
                                  borderRight: "1px solid #e8dfd0",
                                }}
                              >
                                {entry?.administeredAt
                                  ? moment(entry.administeredAt).format("hh:mm A")
                                  : "—"}
                              </td>
                              <td
                                style={{
                                  padding: "12px 14px",
                                  borderBottom: "1px solid #eee6d9",
                                  borderRight: "1px solid #e8dfd0",
                                }}
                              >
                                {entry?.form3?.medicationName || "Medication"}
                              </td>
                              <td
                                style={{
                                  padding: "12px 14px",
                                  borderBottom: "1px solid #eee6d9",
                                  borderRight: "1px solid #e8dfd0",
                                }}
                              >
                                {entry?.reason || entry?.otherReason || "—"}
                              </td>
                              <td
                                style={{
                                  padding: "12px 14px",
                                  borderBottom: "1px solid #eee6d9",
                                  borderRight: "1px solid #e8dfd0",
                                }}
                              >
                                {entry?.comments || "—"}
                              </td>
                              <td
                                style={{
                                  padding: "12px 14px",
                                  borderBottom: "1px solid #eee6d9",
                                }}
                              >
                                {entry?.staff?.name || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  </Box>
                ) : null}
              </>
            )}
          </Box>
        </Modal>
      ) : null}

      {/* ── PDF-only layout — hidden from screen, one month per PDF page ── */}
      {marViewModalOpened && !loading && (
        <Box
          ref={pdfExportRef}
          style={{
            position: "fixed",
            left: "-200000px",
            top: 0,
            background: "#fff",
            pointerEvents: "none",
            zIndex: -1,
          }}
        >
          {monthGroups.map((monthGroup, mIndex) => (
            <Box
              key={monthGroup.key}
              className="pdf-month-section"
              style={{
                background: "#fff",
                borderRadius: 18,
                border: "1px solid #e6dfd2",
                overflow: "hidden",
                marginBottom: 18,
                width: `${260 + 120 + monthGroup.dates.length * 44 + 48}px`,
              }}
            >
              {/* ── Exact frontend header — only on first month page ── */}
              {mIndex === 0 && (
                <>
                  <Box
                    style={{
                      padding: "18px 22px 16px",
                      borderBottom: "1px solid #eee6d9",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 20,
                      alignItems: "flex-start",
                    }}
                  >
                    <Box>
                      <Text fw={800} fz={28} c="#23211c">
                        Medication Administration Record
                      </Text>
                      <Text mt={4} fz={14} c="#7a7468">
                        {facilityName}
                      </Text>
                    </Box>
                    <Box style={{ textAlign: "right" }}>
                      <Text fw={700} fz={14} c="#23211c">
                        Report Month
                      </Text>
                      <Text fz={14} c="#7a7468">
                        {monthDates.length
                          ? `${monthDates[0].format("MMMM D, YYYY")} - ${monthDates[monthDates.length - 1].format("MMMM D, YYYY")}`
                          : "No month selected"}
                      </Text>
                    </Box>
                  </Box>

                  <Box
                    style={{
                      padding: "14px 22px",
                      borderBottom: "1px solid #eee6d9",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <Group spacing={8}>
                      <Box
                        style={{
                          padding: "6px 12px",
                          borderRadius: 999,
                          background: "#f7f1df",
                        }}
                      >
                        <Text fz={12} fw={700} c="#7a5d10">
                          {groupedMedications.length} medications
                        </Text>
                      </Box>
                      <Box
                        style={{
                          padding: "6px 12px",
                          borderRadius: 999,
                          background: "#f0f4fb",
                        }}
                      >
                        <Text fz={12} fw={700} c="#405b85">
                          {commentRows.length} comments
                        </Text>
                      </Box>
                      <Box
                        style={{
                          padding: "6px 12px",
                          borderRadius: 999,
                          background: "#f7eef9",
                        }}
                      >
                        <Text fz={12} fw={700} c="#7a3d86">
                          {prnRows.length} PRN administrations
                        </Text>
                      </Box>
                    </Group>
                  </Box>

                  <Box
                    style={{
                      background: "#242c39",
                      color: "#fff",
                      padding: "16px 22px",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 18,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box>
                      <Text fz={11} tt="uppercase" c="#b9c2cf">
                        Resident
                      </Text>
                      <Text fw={700} fz={18}>
                        {selectedClient?.name || "Unknown"}
                      </Text>
                    </Box>
                    <Box>
                      <Text fz={11} tt="uppercase" c="#b9c2cf">
                        DOB
                      </Text>
                      <Text fw={600} fz={15}>
                        {selectedClient?.form1?.dob
                          ? moment(selectedClient.form1.dob).format(
                              "MM/DD/YYYY",
                            )
                          : "—"}
                      </Text>
                    </Box>
                    <Box style={{ maxWidth: 340 }}>
                      <Text fz={11} tt="uppercase" c="#b9c2cf">
                        Allergies
                      </Text>
                      <Text fw={600} fz={15}>
                        {allergies}
                      </Text>
                    </Box>
                  </Box>

                  <Box
                    style={{
                      padding: "18px 22px",
                      borderBottom: "1px solid #eee6d9",
                    }}
                  >
                    <Text
                      fw={800}
                      fz={14}
                      tt="uppercase"
                      c="#8b8478"
                      mb={14}
                      letterSpacing={1.1}
                    >
                      Contributing Staff
                    </Text>
                    {contributingStaff.length ? (
                      <Box
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(220px, 1fr))",
                          gap: 14,
                        }}
                      >
                        {contributingStaff.map((staff, index) => {
                          const accentPalette = [
                            { background: "#f9edd7", color: "#8a5613" },
                            { background: "#dff2ed", color: "#156c5a" },
                            { background: "#ecebfd", color: "#4f4abb" },
                            { background: "#e4f0fb", color: "#2d69ac" },
                          ];
                          const accent =
                            accentPalette[index % accentPalette.length];
                          return (
                            <Box
                              key={staff.id}
                              style={{
                                border: "1px solid #e4ddcf",
                                borderRadius: 16,
                                padding: "12px 16px",
                                display: "flex",
                                alignItems: "center",
                                gap: 14,
                              }}
                            >
                              <Box
                                style={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: "50%",
                                  background: accent.background,
                                  color: accent.color,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: 800,
                                  fontSize: 18,
                                  flexShrink: 0,
                                }}
                              >
                                {staff.initials}
                              </Box>
                              <Box>
                                <Text fw={700} fz={18} c="#23211c">
                                  {staff.name}
                                </Text>
                                <Text fz={14} c="#6c675e">
                                  Initial: {staff.initials}
                                </Text>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Text fz={14} c="#8c8477">
                        No contributing staff were recorded for this month.
                      </Text>
                    )}
                  </Box>

                  <Box
                    style={{
                      padding: "14px 22px",
                      borderBottom: "1px solid #eee6d9",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 12,
                    }}
                  >
                    {Object.entries(STATUS_COLORS).map(([status, color]) => (
                      <Box
                        key={status}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Box
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 999,
                            background: color,
                            border:
                              color === "#E8E6E0"
                                ? "1px solid #d8d1c2"
                                : "none",
                          }}
                        />
                        <Text fz={12} c="#5f5a50" tt="capitalize">
                          {status}
                        </Text>
                      </Box>
                    ))}
                  </Box>
                </>
              )}

              {/* Month label (shown on every page when multi-month) */}
              {monthGroups.length > 1 && (
                <Box style={{ padding: "10px 22px 0" }}>
                  <Text fw={700} fz={15} c="#23211c">
                    {monthGroup.label}
                  </Text>
                </Box>
              )}

              {/* Horizontal calendar table — same structure as frontend */}
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr style={{ background: "#f8f4ec" }}>
                    <th
                      style={{
                        padding: "12px 14px",
                        borderBottom: "1px solid #e8dfd0",
                        borderRight: "1px solid #e8dfd0",
                        textAlign: "left",
                        minWidth: 260,
                      }}
                    >
                      Medication
                    </th>
                    <th
                      style={{
                        padding: "12px 14px",
                        borderBottom: "1px solid #e8dfd0",
                        borderRight: "1px solid #e8dfd0",
                        textAlign: "left",
                        minWidth: 120,
                      }}
                    >
                      Time Slot
                    </th>
                    {monthGroup.dates.map((date) => (
                      <th
                        key={date.format("YYYY-MM-DD")}
                        style={{
                          padding: "10px 6px",
                          borderBottom: "1px solid #e8dfd0",
                          borderRight: "1px solid #e8dfd0",
                          textAlign: "center",
                          minWidth: 44,
                        }}
                      >
                        <Text fz={10} c="#9b9488">
                          {date.format("ddd")}
                        </Text>
                        <Text fw={700} fz={13} c="#23211c">
                          {date.format("D")}
                        </Text>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupedMedications.length ? (
                    groupedMedications.flatMap((medication) =>
                      medication.slots.map((slot, index) => (
                        <tr key={`${medication.medicationId}-${slot}`}>
                          {index === 0 ? (
                            <td
                              rowSpan={medication.slots.length}
                              style={{
                                padding: "14px",
                                borderBottom: "1px solid #eee6d9",
                                borderRight: "1px solid #e8dfd0",
                                verticalAlign: "top",
                                background: "#fffdfa",
                              }}
                            >
                              <Text fw={700} fz={13} c="#23211c">
                                {medication.medicationName}
                              </Text>
                              <Text mt={3} fz={11} fw={600} c="#5c5549">
                                {medication.dosage || "—"}
                              </Text>
                              {medication.isPrn && medication.instruction ? (
                                <Text mt={4} fz={11} c="#8c8477">
                                  {medication.instruction}
                                </Text>
                              ) : null}
                            </td>
                          ) : null}
                          <td
                            style={{
                              padding: "12px 14px",
                              borderBottom: "1px solid #eee6d9",
                              borderRight: "1px solid #e8dfd0",
                              color: "#4c473f",
                              fontWeight: 600,
                              background: "#fff",
                            }}
                          >
                            {slot}
                          </td>
                          {monthGroup.dates.map((date) => {
                            const dayKey = date.format("YYYY-MM-DD");
                            const entry =
                              medication.rowsBySlot?.[slot]?.[dayKey];
                            const status =
                              `${entry?.medicationType || "upcoming"}`.toLowerCase();
                            const color =
                              STATUS_COLORS[status] || STATUS_COLORS.upcoming;
                            const shouldShowInitials =
                              !!entry?.staff?.name &&
                              status !== "missed" &&
                              status !== "upcoming";
                            return (
                              <td
                                key={`${medication.medicationId}-${slot}-${dayKey}`}
                                style={{
                                  borderBottom: "1px solid #eee6d9",
                                  borderRight: "1px solid #e8dfd0",
                                  textAlign: "center",
                                  padding: 0,
                                  background:
                                    status === "upcoming"
                                      ? "#fff"
                                      : `${color}18`,
                                }}
                              >
                                <Box
                                  style={{
                                    minHeight: 42,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color:
                                      color === "#1a1a1a" ? "#fff" : "#23211c",
                                    fontWeight: 700,
                                    background:
                                      status === "upcoming"
                                        ? "transparent"
                                        : `${color}22`,
                                  }}
                                >
                                  {shouldShowInitials
                                    ? getInitials(entry.staff.name)
                                    : entry
                                      ? "•"
                                      : ""}
                                </Box>
                              </td>
                            );
                          })}
                        </tr>
                      )),
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={monthGroup.dates.length + 2}
                        style={{
                          padding: 28,
                          textAlign: "center",
                          color: "#8c8477",
                        }}
                      >
                        No MAR entries found for the selected month.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          ))}

          {/* ── Comments page — exact copy of frontend comments section ── */}
          <Box
            className="pdf-month-section"
            style={{
              background: "#fff",
              borderRadius: 18,
              border: "1px solid #e6dfd2",
              overflow: "hidden",
              marginBottom: 18,
              width: "900px",
            }}
          >
            <Box
              style={{
                padding: "18px 22px",
                borderBottom: "1px solid #eee6d9",
              }}
            >
              <Text fw={800} fz={24} c="#23211c">
                MAR Administration Comments
              </Text>
            </Box>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead>
                <tr style={{ background: "#f8f4ec" }}>
                  {[
                    "Date",
                    "Time",
                    "Medication",
                    "Status",
                    "Comment",
                    "Staff",
                  ].map((label) => (
                    <th
                      key={label}
                      style={{
                        padding: "12px 14px",
                        borderBottom: "1px solid #e8dfd0",
                        borderRight: "1px solid #e8dfd0",
                        textAlign: "left",
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {commentRows.length ? (
                  commentRows.map((entry, index) => (
                    <tr
                      key={`${entry?._id || entry?.id || "comment"}-${index}`}
                    >
                      <td
                        style={{
                          padding: "12px 14px",
                          borderBottom: "1px solid #eee6d9",
                          borderRight: "1px solid #e8dfd0",
                        }}
                      >
                        {entry?.date
                          ? moment(entry.date).format("MM/DD/YYYY")
                          : "—"}
                      </td>
                      <td
                        style={{
                          padding: "12px 14px",
                          borderBottom: "1px solid #eee6d9",
                          borderRight: "1px solid #e8dfd0",
                        }}
                      >
                        {entry?.createdAt
                          ? moment(entry.createdAt).format("hh:mm A")
                          : "—"}
                      </td>
                      <td
                        style={{
                          padding: "12px 14px",
                          borderBottom: "1px solid #eee6d9",
                          borderRight: "1px solid #e8dfd0",
                        }}
                      >
                        {entry?.form3?.medicationName || "Medication"}
                      </td>
                      <td
                        style={{
                          padding: "12px 14px",
                          borderBottom: "1px solid #eee6d9",
                          borderRight: "1px solid #e8dfd0",
                          textTransform: "capitalize",
                        }}
                      >
                        {entry?.medicationType || "recorded"}
                      </td>
                      <td
                        style={{
                          padding: "12px 14px",
                          borderBottom: "1px solid #eee6d9",
                          borderRight: "1px solid #e8dfd0",
                        }}
                      >
                        {entry?.comments || "—"}
                      </td>
                      <td
                        style={{
                          padding: "12px 14px",
                          borderBottom: "1px solid #eee6d9",
                        }}
                      >
                        {entry?.staff?.name || "—"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        padding: 24,
                        textAlign: "center",
                        color: "#8c8477",
                      }}
                    >
                      No medication comments were recorded for this month.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>

          {/* ── PRN page — exact copy of frontend PRN section ── */}
          {prnRows.length ? (
            <Box
              className="pdf-month-section"
              style={{
                background: "#fff",
                borderRadius: 18,
                border: "1px solid #e6dfd2",
                overflow: "hidden",
                width: "900px",
              }}
            >
              <Box
                style={{
                  padding: "18px 22px",
                  borderBottom: "1px solid #eee6d9",
                }}
              >
                <Text fw={800} fz={24} c="#23211c">
                  PRN Medication Administration
                </Text>
              </Box>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr style={{ background: "#f8f4ec" }}>
                    {[
                      "Date",
                      "Time",
                      "Medication",
                      "Reason",
                      "Comment",
                      "Staff",
                    ].map((label) => (
                      <th
                        key={label}
                        style={{
                          padding: "12px 14px",
                          borderBottom: "1px solid #e8dfd0",
                          borderRight: "1px solid #e8dfd0",
                          textAlign: "left",
                        }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {prnRows.map((entry, index) => (
                    <tr key={`${entry?._id || entry?.id || "prn"}-${index}`}>
                      <td
                        style={{
                          padding: "12px 14px",
                          borderBottom: "1px solid #eee6d9",
                          borderRight: "1px solid #e8dfd0",
                        }}
                      >
                        {entry?.date
                          ? moment(entry.date).format("MM/DD/YYYY")
                          : "—"}
                      </td>
                      <td
                        style={{
                          padding: "12px 14px",
                          borderBottom: "1px solid #eee6d9",
                          borderRight: "1px solid #e8dfd0",
                        }}
                      >
                        {entry?.createdAt
                          ? moment(entry.createdAt).format("hh:mm A")
                          : "—"}
                      </td>
                      <td
                        style={{
                          padding: "12px 14px",
                          borderBottom: "1px solid #eee6d9",
                          borderRight: "1px solid #e8dfd0",
                        }}
                      >
                        {entry?.form3?.medicationName || "Medication"}
                      </td>
                      <td
                        style={{
                          padding: "12px 14px",
                          borderBottom: "1px solid #eee6d9",
                          borderRight: "1px solid #e8dfd0",
                        }}
                      >
                        {entry?.reason || entry?.otherReason || "—"}
                      </td>
                      <td
                        style={{
                          padding: "12px 14px",
                          borderBottom: "1px solid #eee6d9",
                          borderRight: "1px solid #e8dfd0",
                        }}
                      >
                        {entry?.comments || "—"}
                      </td>
                      <td
                        style={{
                          padding: "12px 14px",
                          borderBottom: "1px solid #eee6d9",
                        }}
                      >
                        {entry?.staff?.name || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          ) : null}
        </Box>
      )}
    </>
  );
};

export default AddMarReportModal;
